import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, Upload, Camera, X, RotateCcw,
  FileText, Eye, EyeOff, Shield, Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  TRAILER_OPTIONS, POSITION_OPTIONS, ENDORSEMENT_OPTIONS,
  CDL_CLASS_OPTIONS, EXPERIENCE_OPTIONS,
} from "@/lib/data";

const emptyEmp = { employer: "", title: "", from_date: "", to_date: "" };
const fieldCls =
  "w-full bg-transparent border-b-2 border-border py-3 text-[#0A0A0A] placeholder:text-muted-foreground focus:outline-none focus:border-[#0A0A0A] transition-colors duration-200";
const labelCls = "block font-head text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1";
const sectionHd = "font-head text-base uppercase tracking-[0.25em] text-[#0A0A0A] mb-6 pb-3 border-b-2 border-border";

const STEPS = [
  { label: "Personal",       pct: 0   },
  { label: "Qualifications", pct: 20  },
  { label: "History",        pct: 40  },
  { label: "Documents",      pct: 62  },
  { label: "Sign & Submit",  pct: 78  },
];

const CONSENT_ITEMS = [
  {
    key: "clearinghouse",
    text: "I authorize each partner motor carrier I am referred to, to conduct a full FMCSA Drug & Alcohol Clearinghouse query as part of their hiring process under 49 CFR 382.",
  },
  {
    key: "mvr_reports",
    text: "I authorize each partner motor carrier I am referred to, to obtain MVR, PSP, and DAC reports and contact previous employers per 49 CFR 391.23. I understand Hylas does not obtain these reports.",
  },
  {
    key: "employer_release",
    text: "I authorize previous employers to release all DOT safety performance history including drug/alcohol information to any partner motor carrier that requests it.",
  },
  {
    key: "fcra_acknowledge",
    text: "I acknowledge FCRA disclosure and certify all information is true and complete. I understand this application does not guarantee employment.",
  },
];

const ESIGN_ITEMS = [
  {
    key: "esign",
    text: "I agree to use electronic records and signatures and authorize Hylas Companion to submit applications on my behalf using the signature below.",
  },
  {
    key: "representative",
    text: "I certify I have provided true information and authorize Hylas Companion to be my limited representative for this purpose per FMCSA §391.21 guidance.",
  },
];

const EXCLUSIVITY_ITEMS = [
  {
    key: "exclusivity",
    text: "I authorize Hylas Companion to share my driver application with its partner motor carriers for employment evaluation purposes for a period of 12 months. I understand that Hylas may receive placement compensation from carriers on my behalf. My IP address, device information, and the timestamp of this authorization will be recorded.",
  },
];

const CARRIER_MIRROR_ITEMS = [
  {
    key: "carrier_mirror",
    text: "I Authorize Transmission to Partner Carriers",
  },
];

function formatSSN(value) {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function todayLong() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

export default function ApplyForm({ defaultPosition = "" }) {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = formRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrolled = Math.max(0, -rect.top + 80);
      const total = Math.max(1, el.offsetHeight - window.innerHeight + 160);
      setProgress(Math.min(100, (scrolled / total) * 100));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const currentStep = STEPS.reduce(
    (acc, s, i) => (progress >= s.pct ? i : acc), 0
  );
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    ssn: "", city: "", state: "", cdl_class: "", cdl_experience: "",
    trailer_preference: "", position: defaultPosition, message: "",
    emergency_contact_name: "", emergency_contact_phone: "",
  });
  const [showSSN, setShowSSN] = useState(false);
  const [endorsements, setEndorsements] = useState([]);
  const [employment, setEmployment] = useState([
    { ...emptyEmp }, { ...emptyEmp }, { ...emptyEmp },
  ]);
  const [consents, setConsents] = useState({
    clearinghouse: false,
    mvr_reports: false,
    employer_release: false,
    fcra_acknowledge: false,
    esign: false,
    representative: false,
    exclusivity: false,
    carrier_mirror: false,
    legal_acknowledge: false,
    market: false,
  });
  const [cdlPath, setCdlPath] = useState("");
  const [cdlName, setCdlName] = useState("");
  const [medPath, setMedPath] = useState("");
  const [medName, setMedName] = useState("");
  const [cdlUploading, setCdlUploading] = useState(false);
  const [medUploading, setMedUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Signature canvas
  const canvasRef = useRef(null);
  const [hasSig, setHasSig] = useState(false);
  const isDrawingRef = useRef(false); // use ref instead of state to avoid stale closure
  const cdlFileRef = useRef(null);
  const cdlCamRef = useRef(null);
  const medFileRef = useRef(null);
  const medCamRef = useRef(null);

  // Init canvas — match CSS width, set fixed height, prevent scroll while drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = 160;
    const prevent = (e) => e.preventDefault();
    canvas.addEventListener("touchmove", prevent, { passive: false });
    return () => canvas.removeEventListener("touchmove", prevent);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    isDrawingRef.current = true;
  }, []);

  const doDraw = useCallback((e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSig(true);
  }, []);

  const stopDraw = useCallback(() => { isDrawingRef.current = false; }, []);

  const clearSig = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSSN = (e) => {
    setForm((f) => ({ ...f, ssn: formatSSN(e.target.value) }));
  };

  const setEmp = (i, k) => (e) => {
    setEmployment((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [k]: e.target.value };
      return next;
    });
  };

  const toggleEndorsement = (val) => {
    setEndorsements((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const toggleConsent = (key) => {
    setConsents((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const uploadFile = async (file, setPath, setName, setLoading) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File is too large (max 15 MB)."); return; }
    setLoading(true);
    try {
      const result = await api.uploadFile(file);
      setPath(result.path);
      setName(file.name);
      toast.success("Document uploaded successfully.");
    } catch { toast.error("Upload failed. Please try again."); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.phone.trim()) {
      toast.error("First name and phone number are required.");
      return;
    }
    if (!form.ssn.trim() || form.ssn.replace(/\D/g, "").length < 9) {
      toast.error("A valid 9-digit SSN is required.");
      return;
    }
    if (!Object.values(consents).every(Boolean)) {
      toast.error("Please check all consent boxes before submitting.");
      return;
    }
    if (!hasSig) {
      toast.error("Please draw your signature before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const blob = await new Promise((resolve) =>
        canvasRef.current.toBlob(resolve, "image/png")
      );
      const sigResult = await api.uploadFile(
        new File([blob], "signature.png", { type: "image/png" })
      );

      await api.submitApplication({
        ...form,
        endorsements: endorsements.join(", "),
        employment_history: employment.filter((e) => e.employer.trim()),
        cdl_document_path: cdlPath,
        medical_certificate_path: medPath,
        signature_path: sigResult.path,
        ...consents,
        consent_clearinghouse: consents.clearinghouse,
        consent_mvr_reports: consents.mvr_reports,
        consent_employer_release: consents.employer_release,
        consent_fcra_acknowledge: consents.fcra_acknowledge,
        consent_esign: consents.esign,
        consent_representative: consents.representative,
        consent_exclusivity: consents.exclusivity,
        consent_carrier_mirror: consents.carrier_mirror,
        consent_legal_acknowledge: consents.legal_acknowledge,
        consent_market: consents.market,
        consent_tcpa: true,
        tcpa_consent_text: "",
        tcpa_consent_timestamp: new Date().toISOString(),
        device_info: navigator.userAgent || "",
      });
      setDone(true);
      toast.success("Application received! Our team will call you shortly.");
      navigate("/track", { state: { phone: form.phone, fresh: true } });
    } catch {
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-success" className="bg-white border border-border p-10 md:p-14 text-center">
        <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" strokeWidth={1.5} />
        <h3 className="font-head text-2xl md:text-3xl uppercase tracking-[0.15em] mb-3">You're In Motion</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Thanks, {form.first_name}. Your application is in — our recruiting team will reach out by phone soon to get you rolling.
        </p>
        <p className="mt-5 text-sm text-muted-foreground">
          Check your status anytime at{" "}
          <a href="/track" className="text-primary underline">hylascompanion.us/track</a>
        </p>
      </div>
    );
  }

  const allConsentsChecked = Object.values(consents).every(Boolean);

  return (
    <form ref={formRef} onSubmit={submit} data-testid="apply-form" className="bg-white border border-border flex items-start">

      {/* ── Main form content ── */}
      <div className="flex-1 min-w-0 px-6 md:px-10 space-y-10 py-10">

      {/* ── 01 PERSONAL INFO ── */}
      <div>
        <h3 className={sectionHd}>01 · Personal Information</h3>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <label className={labelCls}>First Name *</label>
            <input data-testid="apply-first-name" value={form.first_name} onChange={set("first_name")} className={fieldCls} placeholder="John" />
          </div>
          <div>
            <label className={labelCls}>Last Name</label>
            <input data-testid="apply-last-name" value={form.last_name} onChange={set("last_name")} className={fieldCls} placeholder="Doe" />
          </div>
          <div>
            <label className={labelCls}>Phone *</label>
            <input data-testid="apply-phone" value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="apply-email" value={form.email} onChange={set("email")} className={fieldCls} placeholder="john@email.com" />
          </div>

          {/* ── Phone field above ── */}
          <div>
            <label className={labelCls}>City</label>
            <input data-testid="apply-city" value={form.city} onChange={set("city")} className={fieldCls} placeholder="Dallas" />
          </div>
          <div>
            <label className={labelCls}>State</label>
            <input data-testid="apply-state" value={form.state} onChange={set("state")} className={fieldCls} placeholder="TX" />
          </div>
          {/* SSN — full-width, mandatory */}
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Social Security Number (SSN) *{" "}
              <span className="font-sans normal-case tracking-normal text-[11px] text-muted-foreground ml-1">
                — used for FMCSA background checks only
              </span>
            </label>
            <div className="relative">
              <input
                data-testid="apply-ssn"
                type={showSSN ? "text" : "password"}
                value={form.ssn}
                onChange={handleSSN}
                className={`${fieldCls} pr-12`}
                placeholder="XXX-XX-XXXX"
                inputMode="numeric"
                maxLength={11}
                autoComplete="off"
              />
              <button
                type="button"
                data-testid="ssn-toggle"
                onClick={() => setShowSSN((v) => !v)}
                className="absolute right-0 bottom-3 text-muted-foreground hover:text-[#0A0A0A] transition-colors"
                tabIndex={-1}
                aria-label={showSSN ? "Hide SSN" : "Show SSN"}
              >
                {showSSN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Lock className="w-3 h-3" /> Encrypted and stored securely. Never shared without your authorization.
            </p>
          </div>
        </div>
      </div>

      {/* ── 02 PAY PACKAGE ── */}
      <div>
        <h3 className={sectionHd}>02 · Pay Package Interest</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {POSITION_OPTIONS.map((p) => (
            <button
              type="button"
              key={p}
              data-testid={`pkg-${p.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, position: f.position === p ? "" : p }))}
              className={`text-left px-4 py-3 border-2 transition-all duration-200 ${
                form.position === p
                  ? "bg-[#0A0A0A] text-primary border-[#0A0A0A]"
                  : "bg-transparent text-[#0A0A0A] border-border hover:border-[#0A0A0A]"
              }`}
            >
              <span className="font-head text-xs uppercase tracking-[0.12em] leading-snug">{p}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── 03 CDL & EXPERIENCE ── */}
      <div>
        <h3 className={sectionHd}>03 · CDL & Experience</h3>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <label className={labelCls}>CDL Class</label>
            <select data-testid="apply-cdl-class" value={form.cdl_class} onChange={set("cdl_class")} className={fieldCls}>
              <option value="">Select...</option>
              {CDL_CLASS_OPTIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>OTR Experience</label>
            <select data-testid="apply-experience" value={form.cdl_experience} onChange={set("cdl_experience")} className={fieldCls}>
              <option value="">Select...</option>
              {EXPERIENCE_OPTIONS.map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Trailer Preference</label>
            <select data-testid="apply-trailer" value={form.trailer_preference} onChange={set("trailer_preference")} className={fieldCls}>
              <option value="">Select...</option>
              {TRAILER_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-6">
          <label className={labelCls}>Endorsements</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ENDORSEMENT_OPTIONS.map((en) => (
              <button
                type="button"
                key={en}
                data-testid={`endorsement-${en.toLowerCase().replace(/[^a-z]/g, "")}`}
                onClick={() => toggleEndorsement(en)}
                className={`font-head text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors duration-200 ${
                  endorsements.includes(en)
                    ? "bg-[#0A0A0A] text-primary border-[#0A0A0A]"
                    : "bg-transparent text-[#0A0A0A] border-border hover:border-[#0A0A0A]"
                }`}
              >
                {en}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── 04 EMPLOYMENT HISTORY ── */}
      <div>
        <h3 className={sectionHd}>04 · Employment History (Last 3 Years)</h3>
        <p className="text-sm text-muted-foreground mb-6">List your most recent employers. Fill in what applies.</p>
        <div className="space-y-8">
          {employment.map((entry, i) => (
            <div key={i} className="border border-border p-5">
              <p className="font-head text-xs uppercase tracking-[0.25em] text-primary mb-4">Position {i + 1}</p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                <div>
                  <label className={labelCls}>Employer Name</label>
                  <input data-testid={`emp-employer-${i}`} value={entry.employer} onChange={setEmp(i, "employer")} className={fieldCls} placeholder="Company Name" />
                </div>
                <div>
                  <label className={labelCls}>Job Title / Position</label>
                  <input data-testid={`emp-title-${i}`} value={entry.title} onChange={setEmp(i, "title")} className={fieldCls} placeholder="OTR Driver" />
                </div>
                <div>
                  <label className={labelCls}>From (MM/YYYY)</label>
                  <input data-testid={`emp-from-${i}`} value={entry.from_date} onChange={setEmp(i, "from_date")} className={fieldCls} placeholder="01/2022" />
                </div>
                <div>
                  <label className={labelCls}>To (MM/YYYY or Present)</label>
                  <input data-testid={`emp-to-${i}`} value={entry.to_date} onChange={setEmp(i, "to_date")} className={fieldCls} placeholder="Present" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 05 EMERGENCY CONTACT ── */}
      <div>
        <h3 className={sectionHd}>05 · Emergency Contact</h3>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <label className={labelCls}>Contact Name</label>
            <input data-testid="apply-emergency-name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} className={fieldCls} placeholder="Jane Doe" />
          </div>
          <div>
            <label className={labelCls}>Contact Phone</label>
            <input data-testid="apply-emergency-phone" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} className={fieldCls} placeholder="(555) 987-6543" />
          </div>
        </div>
      </div>

      {/* ── 06 DOCUMENTS ── */}
      <div>
        <h3 className={sectionHd}>06 · Documents <span className="text-muted-foreground font-sans normal-case tracking-normal text-xs">(Optional)</span></h3>
        <p className="text-sm text-muted-foreground mb-6">Upload your CDL and Medical Certificate for faster processing. Images or PDF accepted. Max 15 MB each.</p>
        <div className="grid sm:grid-cols-2 gap-8">
          <DocUpload label="CDL License" testId="cdl" uploading={cdlUploading} uploaded={!!cdlPath} filename={cdlName} fileRef={cdlFileRef} camRef={cdlCamRef} onFile={(f) => uploadFile(f, setCdlPath, setCdlName, setCdlUploading)} onClear={() => { setCdlPath(""); setCdlName(""); }} />
          <DocUpload label="Medical Certificate" testId="med" uploading={medUploading} uploaded={!!medPath} filename={medName} fileRef={medFileRef} camRef={medCamRef} onFile={(f) => uploadFile(f, setMedPath, setMedName, setMedUploading)} onClear={() => { setMedPath(""); setMedName(""); }} />
        </div>
      </div>

      {/* ── HYLAS MARKET CONSENT (optional — not required for submission) ── */}
      <div data-testid="market-consent-section">
        <div className="border-l-4 border-[#F5C842] bg-[#F5C842]/5 px-5 py-4 mb-4">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-[#0A0A0A] mb-2">
            Hylas Market
          </p>
          <p className="text-sm text-foreground/70 leading-relaxed">
            Hylas Companion operates a driver transfer market where carriers compete for top drivers.
            By checking the box below, you authorize Hylas Companion to display your name, state,
            and a cropped face image (from your uploaded CDL) on your Market candidate card.
            Your CDL number, address, phone and email remain confidential.
            You may withdraw this consent at any time by contacting us.{" "}
            <Link to="/market/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-[#0A0A0A]">
              Market Terms
            </Link>
          </p>
        </div>
        <ConsentBox
          id="consent-market"
          checked={consents.market}
          onChange={() => toggleConsent("market")}
          text="I authorize Hylas Companion to display my name, state and cropped CDL face image on the Hylas Market candidate card to receive placement offers from carriers."
        />
      </div>

      {/* ── 07 CONSENTS & CERTIFICATION ── */}
      <div data-testid="consents-section">
        <h3 className={sectionHd}>07 · Consents & Certification</h3>

        {/* ── FCRA block ── */}
        <div className="border-l-4 border-[#0A0A0A] bg-muted/40 px-5 py-4 mb-6">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-[#0A0A0A] mb-3">
            FCRA Disclosure — Important — Please Read
          </p>
          <div className="space-y-2 text-sm text-foreground/75 leading-relaxed">
            <p>
              Andrej Sasaroga DBA Hylas Companion ("Hylas") is a driver recruiting and marketing service only.{" "}
              Hylas is not a motor carrier, not an employer, and not a consumer reporting agency.{" "}
              Hylas does not obtain MVR, PSP, Clearinghouse, DAC, or background reports.
            </p>
            <p>
              Each partner motor carrier you are referred to is your prospective employer. Under FCRA and FMCSA regulations (49 CFR 391.23, 49 CFR 382),{" "}
              each carrier will independently request your authorization to obtain consumer reports as part of their own hiring process.
            </p>
            <p>I certify this application was completed by me and all entries are true and complete. I understand false statements may lead to disqualification or termination.</p>
          </div>
        </div>

        {/* FCRA checkboxes */}
        <div className="space-y-3 mb-10">
          {CONSENT_ITEMS.map((item) => (
            <ConsentBox
              key={item.key}
              id={`consent-${item.key}`}
              checked={consents[item.key]}
              onChange={() => toggleConsent(item.key)}
              text={item.text}
            />
          ))}
        </div>

        {/* ── Third-Party Submission block ── */}
        <div className="border-l-4 border-primary bg-primary/5 px-5 py-4 mb-6">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-[#0A0A0A] mb-3">
            Authorization for Third-Party Submission – Limited Power of Attorney
          </p>
          <div className="space-y-2 text-sm text-foreground/75 leading-relaxed">
            <p>I authorize Hylas Companion, its agents and assigns, to act as my authorized representative and preparer for the purpose of submitting employment applications for truck driving positions on my behalf to motor carriers and their service providers (including Tenstreet, IntelliApp, Driver Pulse).</p>
            <p>I understand Hylas Companion may transfer information provided in this application, including my personal data, employment history, and safety history, to such carriers to facilitate my employment.</p>
            <p>I authorize Hylas Companion to affix or apply my electronic signature, as drawn/typed below, to such applications and related FMCSA-required authorizations, and I agree that such signature shall have the same force and effect as if I had personally signed the carrier's application. I retain full responsibility for the truthfulness of the information.</p>
            <p>This authorization is valid for 1 year from the date below and may be revoked in writing at any time. I have read and agree to the E-SIGN disclosure that electronic signatures are valid. IP and timestamp will be recorded.</p>
          </div>
        </div>

        {/* E-Sign checkboxes */}
        <div className="space-y-3">
          {ESIGN_ITEMS.map((item) => (
            <ConsentBox
              key={item.key}
              id={`consent-${item.key}`}
              checked={consents[item.key]}
              onChange={() => toggleConsent(item.key)}
              text={item.text}
            />
          ))}
        </div>

        {/* ── Application Sharing Authorization ── */}
        <div className="border-l-4 border-primary bg-primary/5 px-5 py-4 mt-8 mb-4">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-[#0A0A0A] mb-3">
            Application Sharing Authorization
          </p>
          <div className="space-y-2 text-sm text-foreground/75 leading-relaxed">
            <p>By checking the box, you authorize Hylas to share your application with carriers for 12 months. You understand Hylas may be paid by carriers. IP/device/timestamp recorded.</p>
          </div>
        </div>
        <div className="space-y-3">
          {EXCLUSIVITY_ITEMS.map((item) => (
            <ConsentBox
              key={item.key}
              id={`consent-${item.key}`}
              checked={consents[item.key]}
              onChange={() => toggleConsent(item.key)}
              text={item.text}
            />
          ))}
        </div>

        {/* ── Multi-Carrier Lead & Application Transmission Authorization ── */}
        <div className="mt-6 border-l-4 border-blue-500/40 bg-blue-500/[0.04] px-5 py-4 mb-3">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-[#0A0A0A] mb-3">
            Multi-Carrier Lead &amp; Application Transmission Authorization
          </p>
          <div className="space-y-3 text-sm text-foreground/75 leading-relaxed">
            <p>
              I understand Andrej Sasaroga DBA Hylas Companion ("Hylas") is a driver recruiting and marketing service only,{" "}
              not a motor carrier, not an employer, and not a consumer reporting agency.{" "}
              Hylas does not obtain MVR, PSP, Clearinghouse reports, or background checks.
            </p>
            <p className="font-medium text-foreground/80">By checking this box, I:</p>
            <ol className="space-y-2 pl-4 list-decimal">
              <li>
                Authorize Hylas to transmit my completed driver application and contact information to its partner motor carriers listed at{" "}
                <a href="https://hylascompanion.com/partners" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
                  hylascompanion.com/partners
                </a>{" "}
                for employment consideration.
              </li>
              <li>
                Understand each partner carrier is my prospective employer and will separately request my authorizations to obtain MVR, PSP, Clearinghouse, and consumer reports under FCRA and FMCSA regulations.{" "}
                Hylas will not obtain these reports on their behalf.
              </li>
              <li>
                Consent to be contacted by Hylas and its partner carriers via phone, SMS, and email about driving opportunities.{" "}
                I understand consent is not a condition of employment, I can revoke at any time in writing to{" "}
                <a href="mailto:contact@hylascompanion.com" className="text-primary underline underline-offset-2">
                  contact@hylascompanion.com
                </a>
                , and message and data rates may apply. This consent is valid for 1 year or until revoked.
              </li>
            </ol>
          </div>
        </div>
        <div className="space-y-3">
          {CARRIER_MIRROR_ITEMS.map((item) => (
            <ConsentBox
              key={item.key}
              id={`consent-${item.key}`}
              checked={consents[item.key]}
              onChange={() => toggleConsent(item.key)}
              text={item.text}
            />
          ))}
        </div>

        {/* All-consents required hint */}
        {!allConsentsChecked && (
          <p className="mt-4 text-xs text-amber-600 font-medium">
            All ten boxes must be checked to submit your application.
          </p>
        )}
      </div>

      {/* ── 07b LEGAL ACKNOWLEDGEMENT ── */}
      <div data-testid="legal-consent-section">
        <div className="border-l-4 border-primary/50 bg-[#0A0A0A]/[0.03] px-5 py-4 mb-4">
          <p className="font-head text-xs uppercase tracking-[0.25em] text-[#0A0A0A] mb-2">
            Terms of Service &amp; Privacy Policy
          </p>
          <p className="text-sm text-foreground/70 leading-relaxed">
            By submitting this application you confirm that you have read and agree to our{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-[#0A0A0A]">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-[#0A0A0A]">
              Privacy Policy
            </Link>
            , including our collection and use of your personal, CDL, and employment information for recruitment purposes.
          </p>
        </div>
        <ConsentBoxWithLinks
          id="consent-legal_acknowledge"
          checked={consents.legal_acknowledge}
          onChange={() => toggleConsent("legal_acknowledge")}
        >
          I have read and agree to the{" "}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-[#0A0A0A] font-medium">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-[#0A0A0A] font-medium">
            Privacy Policy
          </Link>
          . I understand how my personal and driver data will be collected, used, and shared with carriers and third-party platforms (including Tenstreet and IntelliApp) as described in the Privacy Policy.
        </ConsentBoxWithLinks>
      </div>

      {/* ── HYLAS MARKET CONSENT moved to top — kept here as empty placeholder ── */}

      {/* ── 08 SIGNATURE ── */}
      <div>
        <h3 className={sectionHd}>08 · E-Signature</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Draw your signature with your mouse or finger. By signing, you agree to all authorizations above.
        </p>

        <div className="border-2 border-border relative bg-white" style={{ touchAction: "none" }}>
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            className="w-full cursor-crosshair"
            style={{ display: "block", height: "160px", touchAction: "none" }}
            onMouseDown={startDraw}
            onMouseMove={doDraw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={doDraw}
            onTouchEnd={stopDraw}
            onTouchCancel={stopDraw}
          />
          {!hasSig && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-2">
              <span className="font-head text-xs uppercase tracking-[0.3em] text-muted-foreground">Sign Here</span>
              <span className="text-xs text-muted-foreground/60">Mouse or finger</span>
            </div>
          )}
          {hasSig && (
            <button
              type="button"
              data-testid="clear-signature"
              onClick={clearSig}
              className="absolute top-2 right-2 flex items-center gap-1 font-head text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-[#0A0A0A] transition-colors bg-white/80 px-2 py-1"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        {/* X line + date */}
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-muted-foreground">
            X _____ By signing, I authorize Hylas Companion per the disclosures above.
          </p>
          <p className="font-head text-xs uppercase tracking-[0.15em] text-muted-foreground" data-testid="sig-date">
            Date: {todayLong()}
          </p>
        </div>
      </div>

      {/* ── 09 ADDITIONAL NOTES ── */}
      <div>
        <h3 className={sectionHd}>09 · Additional Notes</h3>
        <label className={labelCls}>Anything else you'd like us to know?</label>
        <textarea
          data-testid="apply-message"
          value={form.message}
          onChange={set("message")}
          rows={3}
          className={`${fieldCls} resize-none`}
          placeholder="Route preferences, home time needs, questions for the recruiter..."
        />
      </div>

      <div className="space-y-4">
        <button
          type="submit"
          data-testid="apply-submit-btn"
          disabled={!allConsentsChecked || submitting}
          className="w-full font-head text-base uppercase tracking-[0.2em] bg-primary text-black py-5 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
        >
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : "Submit Application"}
        </button>

        {/* Security footer */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Shield className="w-3.5 h-3.5" />
          <span>Secure 256-bit encrypted</span>
          <span className="text-border">·</span>
          <span>Takes under 3 minutes</span>
        </div>
        <p className="text-center text-xs text-muted-foreground">Drivers never pay a dime. No fees, ever.</p>
      </div>
      </div>{/* end main content */}

      {/* ── Vertical step indicator — right side, desktop only ── */}
      <aside
        data-testid="apply-step-sidebar"
        className="hidden lg:flex flex-col items-center sticky top-28 self-start border-l border-border shrink-0 py-8 px-2.5"
        style={{ width: 52 }}
      >
        {STEPS.map((s, i) => {
          const done = i < currentStep;
          const active = i === currentStep;
          return (
            <div key={i} className="flex flex-col items-center w-full">
              {/* numbered square */}
              <div
                title={s.label}
                className={`w-7 h-7 flex items-center justify-center border-2 transition-all duration-300 ${
                  done
                    ? "bg-primary border-primary"
                    : active
                    ? "bg-[#0A0A0A] border-primary"
                    : "bg-white border-border"
                }`}
              >
                {done ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-black" strokeWidth={2.5} />
                ) : (
                  <span className={`font-head text-[9px] leading-none ${active ? "text-primary" : "text-foreground/30"}`}>
                    {i + 1}
                  </span>
                )}
              </div>

              {/* rotated label */}
              <span
                className={`font-head text-[7px] uppercase my-1.5 transition-colors duration-300 whitespace-nowrap ${
                  active ? "text-primary" : done ? "text-foreground/50" : "text-foreground/20"
                }`}
                style={{ writingMode: "vertical-lr", letterSpacing: "0.15em" }}
              >
                {s.label}
              </span>

              {/* connector line */}
              {i < STEPS.length - 1 && (
                <div
                  className={`w-px h-4 transition-colors duration-300 ${done ? "bg-primary" : "bg-border"}`}
                />
              )}
            </div>
          );
        })}
      </aside>

    </form>
  );
}

/* ── Consent checkbox ── */
function ConsentBox({ id, checked, onChange, text }) {
  return (
    <label
      htmlFor={id}
      data-testid={id}
      className={`flex items-start gap-3 p-4 border-2 cursor-pointer transition-all duration-200 ${
        checked ? "border-[#0A0A0A] bg-[#0A0A0A]/[0.03]" : "border-border hover:border-[#0A0A0A]/40"
      }`}
    >
      <div className="relative shrink-0 mt-0.5">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
            checked ? "bg-[#0A0A0A] border-[#0A0A0A]" : "bg-white border-border"
          }`}
        >
          {checked && (
            <svg className="w-3 h-3 text-primary" fill="none" viewBox="0 0 12 12">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
      <p className="text-sm text-foreground/80 leading-relaxed select-none">{text}</p>
    </label>
  );
}

/* ── Consent checkbox with JSX children (for links) ── */
function ConsentBoxWithLinks({ id, checked, onChange, children }) {
  return (
    <label
      htmlFor={id}
      data-testid={id}
      className={`flex items-start gap-3 p-4 border-2 cursor-pointer transition-all duration-200 ${
        checked ? "border-primary bg-primary/[0.04]" : "border-border hover:border-primary/40"
      }`}
    >
      <div className="relative shrink-0 mt-0.5">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${
            checked ? "bg-primary border-primary" : "bg-white border-border"
          }`}
        >
          {checked && (
            <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 12 12">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
      <div className="text-sm text-foreground/80 leading-relaxed select-none">{children}</div>
    </label>
  );
}

/* ── Document upload widget ── */
function DocUpload({ label, testId, uploading, uploaded, filename, fileRef, camRef, onFile, onClear }) {
  return (
    <div data-testid={`doc-upload-${testId}`}>
      <label className="block font-head text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{label}</label>
      {uploaded ? (
        <div className="flex items-center gap-3 border border-primary bg-primary/5 px-4 py-3">
          <FileText className="w-5 h-5 text-primary shrink-0" />
          <span className="text-sm font-medium flex-1 truncate">{filename}</span>
          <button type="button" onClick={onClear} data-testid={`clear-${testId}`} className="text-muted-foreground hover:text-[#0A0A0A]">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex gap-3">
          <button type="button" data-testid={`upload-file-${testId}`} disabled={uploading} onClick={() => fileRef.current?.click()}
            className="flex-1 flex items-center justify-center gap-2 border-2 border-border py-3 font-head text-xs uppercase tracking-[0.15em] hover:border-[#0A0A0A] transition-colors disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading" : "Upload File"}
          </button>
          <button type="button" data-testid={`camera-${testId}`} disabled={uploading} onClick={() => camRef.current?.click()}
            className="flex-1 flex items-center justify-center gap-2 border-2 border-border py-3 font-head text-xs uppercase tracking-[0.15em] hover:border-[#0A0A0A] transition-colors disabled:opacity-50">
            <Camera className="w-4 h-4" /> Take Photo
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid={`file-input-${testId}`} onChange={(e) => onFile(e.target.files[0])} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`cam-input-${testId}`} onChange={(e) => onFile(e.target.files[0])} />
    </div>
  );
}
